import { firestore } from '@src/api/Firebase/Firebase';
import { collection } from 'firebase/firestore';
import { Input } from '@api/Inputs/Input';
export interface BranchOfficeInventory {
    id?: string;
    distributionCenterId?: string;
    branchOfficeId: string;
    /**
     * El insumo. ⚠️ En el DOCUMENTO es un snapshot del día que nació el renglón (el
     * server lo escribe completo en `applyToBranchInventory`) y **nadie lo actualiza**:
     * `InputServ.saveInput` sincroniza la copia del CEDIS, no la de las sucursales. Al
     * 13-oct-2026, 338 de 837 renglones no traían umbrales y 211 tenían otro
     * rendimiento. Por eso `getInventory` lo REEMPLAZA por el insumo del catálogo
     * (`withCatalogInput`) y lo que la pantalla deriva —presentaciones, costo—
     * sale siempre de ahí. El snapshot queda como fósil: solo sirve para
     * `where("input.id", "==")` en el server y para un insumo ya borrado. El ESTADO
     * ya no sale de umbrales del catálogo sino de días de inventario
     * (`@api/Inputs/StockStatus`, F4).
     */
    input: Input;
    /**
     * Contadores HEREDADOS: `stock` = presentaciones completas y `currentUnit` =
     * lo que queda de la presentación abierta. Desde F5 son DERIVADOS de
     * `totalUnits` — el motor de inventario los recalcula al aplicar cada
     * movimiento para no romper las pantallas que ya los leen.
     */
    currentUnit: number;
    stock: number;
    /**
     * Saldo REAL en unidades base del insumo (F5). Es lo que manda: el contador de
     * dos niveles se equivocaba cuando una sola venta consumía más de una
     * presentación de golpe (descontaba UNA sola). Ausente en inventarios previos
     * a F5 → se deriva de `stock`/`currentUnit` la primera vez que se toca.
     */
    totalUnits?: number;
}

/**
 * Cada renglón con el insumo DEL CATÁLOGO en vez del snapshot guardado (plan de
 * inventario F0). Un insumo que ya no está en el catálogo conserva su copia: la
 * pantalla lo muestra como "(insumo borrado)" y no pierde el saldo.
 */
export const withCatalogInput = (rows: BranchOfficeInventory[], catalogInputs: Input[]): BranchOfficeInventory[] => {
    const byId = new Map(catalogInputs.filter((input) => !!input.id).map((input) => [input.id as string, input]));
    return rows.map((row) => {
        const fresh = row.input?.id ? byId.get(row.input.id) : undefined;
        return fresh ? { ...row, input: fresh } : row;
    });
};

/**
 * Lo disponible en unidades base: `totalUnits` manda. Un renglón anterior a F5 no
 * lo trae y se deriva de los contadores viejos, igual que lo hace el server.
 */
export const getAvailableUnits = (row: Pick<BranchOfficeInventory, 'totalUnits' | 'stock' | 'currentUnit' | 'input'>): number => {
    if (Number.isFinite(Number(row.totalUnits))) {
        return Number(row.totalUnits);
    }
    const unitValue = Number(row.input?.unit?.value) || 1;
    return (Number(row.stock) || 0) * unitValue + (Number(row.currentUnit) || 0);
};

/**
 * Lo disponible en PRESENTACIONES del catálogo (puede ser fracción: 2.5 galones).
 * Es contra lo que se comparan los umbrales del insumo. Sin rendimiento válido no
 * hay presentaciones que contar: 0.
 */
export const getAvailablePresentations = (row: Pick<BranchOfficeInventory, 'totalUnits' | 'stock' | 'currentUnit' | 'input'>): number => {
    const unitValue = Number(row.input?.unit?.value);
    if (!Number.isFinite(unitValue) || unitValue <= 0) {
        return 0;
    }
    return getAvailableUnits(row) / unitValue;
};

/** `1,800` · `2.5` · `-439`: hasta 2 decimales con separador de miles; nunca "NaN". */
export const formatQuantity = (value: number, maximumFractionDigits = 2): string =>
    new Intl.NumberFormat('es-MX', { maximumFractionDigits }).format(Number.isFinite(value) ? value : 0);

/**
 * Cómo se compra y cuánto rinde, en UN texto: "1.8 L = 1,800 ml" (F0b: eran dos
 * columnas, "Presentación" y "Unidad Total", en una tabla que ya no cabía en la
 * tablet). Cuando la presentación ES la unidad base ("50 pza = 50 pza") se dice una vez.
 */
export const describePresentation = (input?: Pick<Input, 'presentation' | 'unit'>): string => {
    const presentation = input?.presentation;
    const unit = input?.unit;
    const pres =
        presentation && Number.isFinite(Number(presentation.value))
            ? `${formatQuantity(Number(presentation.value))} ${presentation.unit || ''}`.trim()
            : '';
    const base = unit && Number.isFinite(Number(unit.value)) ? `${formatQuantity(Number(unit.value))} ${unit.unit || ''}`.trim() : '';
    if (!pres && !base) return '—';
    if (!pres || !base || pres === base) return pres || base;
    return `${pres} = ${base}`;
};

/**
 * Lo disponible dicho en presentaciones, para quien piensa en galones y bolsas:
 * "≈ 3.2 × 3.7 L". Vacío cuando la presentación rinde una sola unidad base (sería
 * repetir el número de arriba).
 */
export const describeAvailablePresentations = (
    row: Pick<BranchOfficeInventory, 'totalUnits' | 'stock' | 'currentUnit' | 'input'>
): string => {
    const unitValue = Number(row.input?.unit?.value);
    if (!Number.isFinite(unitValue) || unitValue <= 1) {
        return '';
    }
    const presentation = row.input?.presentation;
    const label = presentation ? `${formatQuantity(Number(presentation.value))} ${presentation.unit || ''}`.trim() : '';
    return `≈ ${formatQuantity(getAvailablePresentations(row), 1)}${label ? ` × ${label}` : ' presentaciones'}`;
};

export const BRANCH_OFFICE_INVENTORY_COLLECTION = 'branch-office-inventories';
export const branchOfficeInventoriesRef = collection(firestore, BRANCH_OFFICE_INVENTORY_COLLECTION);
